"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MGRS = void 0;
const grid_js_1 = require("@ngageoint/grid-js");
const GridType_1 = require("./grid/GridType");
const sprintf = __importStar(require("sprintf-js"));
const GridTypeUtils_1 = require("./grid/GridTypeUtils");
const UTM_1 = require("./utm/UTM");
const GridZones_1 = require("./gzd/GridZones");
const MGRSUtils_1 = require("./MGRSUtils");
/**
 * Military Grid Reference System Coordinate
 */
class MGRS {
    /**
     * Constructor
     *
     * @param zone
     *            zone number
     * @param band
     *            band letter
     * @param column
     *            column letter
     * @param row
     *            row letter
     * @param easting
     *            easting
     * @param northing
     *            northing
     */
    constructor(zone, band, column, row, easting, northing) {
        this.zone = zone;
        this.band = band;
        this.column = column;
        this.row = row;
        this.easting = easting;
        this.northing = northing;
    }
    /**
     * Create
     *
     * @param zone
     *            zone number
     * @param band
     *            band letter
     * @param easting
     *            easting
     * @param northing
     *            northing
     * @return MGRS
     */
    static create(zone, band, easting, northing, column, row) {
        if (!column) {
            column = this.getColumnLetter(zone, easting);
        }
        if (!row) {
            row = this.getRowLetter(zone, northing);
        }
        return new MGRS(zone, band, column, row, easting, northing);
    }
    /**
     * Get the zone number
     *
     * @return zone number
     */
    getZone() {
        return this.zone;
    }
    /**
     * Get the band letter
     *
     * @return band letter
     */
    getBand() {
        return this.band;
    }
    /**
     * Get the column letter
     *
     * @return column letter
     */
    getColumn() {
        return this.column;
    }
    /**
     * Get the row letter
     *
     * @return row letter
     */
    getRow() {
        return this.row;
    }
    /**
     * Get the easting
     *
     * @return easting
     */
    getEasting() {
        return this.easting;
    }
    /**
     * Get the northing
     *
     * @return northing
     */
    getNorthing() {
        return this.northing;
    }
    /**
     * Get the hemisphere
     *
     * @return hemisphere
     */
    getHemisphere() {
        return MGRSUtils_1.MGRSUtils.getHemisphere(this.band);
    }
    /**
     * Get the MGRS coordinate with specified grid precision
     *
     * @param type
     *            grid type precision
     * @return MGRS coordinate
     */
    coordinate(type) {
        let mgrs = '';
        if (type === null || type === undefined) {
            type = GridType_1.GridType.METER;
        }
        mgrs += this.zone;
        mgrs += this.band;
        if (type.valueOf() !== GridType_1.GridType.GZD.valueOf()) {
            mgrs += this.column;
            mgrs += this.row;
            if (type !== GridType_1.GridType.HUNDRED_KILOMETER) {
                mgrs += this.getEastingAndNorthing(type);
            }
        }
        return mgrs.toString();
    }
    /**
     * Get the easting and northing concatenated value in the grid type
     * precision
     *
     * @param type
     *            grid type precision
     * @return easting and northing value
     */
    getEastingAndNorthing(type) {
        const accuracy = 5 - ~~Math.log10(type);
        // TODO apply locale
        const easting = sprintf.sprintf('%05d', this.easting);
        const northing = sprintf.sprintf('%05d', this.northing);
        return easting.substring(0, accuracy) + northing.substring(0, accuracy);
    }
    /**
     * Get the MGRS coordinate with the accuracy number of digits in the easting
     * and northing values. Accuracy must be inclusively between 0
     * ({@link GridType#HUNDRED_KILOMETER}) and 5 ({@link GridType#METER}).
     *
     * @param accuracy
     *            accuracy digits between 0 (inclusive) and 5 (inclusive)
     * @return MGRS coordinate
     */
    coordinateFromAccuracy(accuracy) {
        return this.coordinate(GridTypeUtils_1.GridTypeUtils.withAccuracy(accuracy));
    }
    /**
     * Get the MGRS coordinate grid precision
     *
     * @return grid type precision
     */
    precision() {
        return GridTypeUtils_1.GridTypeUtils.withAccuracy(this.accuracy());
    }
    /**
     * Get the MGRS coordinate accuracy number of digits
     *
     * @return accuracy digits
     */
    accuracy() {
        let accuracy = 5;
        for (let accuracyLevel = 10; accuracyLevel <= 100000; accuracyLevel *= 10) {
            if (this.easting % accuracyLevel !== 0 || this.northing % accuracyLevel !== 0) {
                break;
            }
            accuracy--;
        }
        return accuracy;
    }
    /**
     * Get the two letter column and row 100k designator
     *
     * @return the two letter column and row 100k designator
     */
    getColumnRowId() {
        return this.column + this.row;
    }
    /**
     * Get the GZD grid zone
     *
     * @return GZD grid zone
     */
    getGridZone() {
        return GridZones_1.GridZones.getGridZoneFromMGRS(this);
    }
    /**
     * Convert to a point
     *
     * @return point
     */
    toPoint() {
        return this.toUTM().toPoint();
    }
    /**
     * Convert to UTM coordinate
     *
     * @return UTM
     */
    toUTM() {
        const easting = this.getUTMEasting();
        const northing = this.getUTMNorthing();
        const hemisphere = this.getHemisphere();
        return UTM_1.UTM.create(this.zone, hemisphere, easting, northing);
    }
    /**
     * Get the UTM easting
     *
     * @return UTM easting
     */
    getUTMEasting() {
        // get easting specified by e100k
        const columnLetters = MGRS.getColumnLetters(this.zone);
        const columnIndex = columnLetters.indexOf(this.column) + 1;
        // index+1 since A (index 0) -> 1*100e3, B (index 1) -> 2*100e3, etc.
        const e100kNum = columnIndex * 100000.0; // e100k in meters
        return e100kNum + this.easting;
    }
    /**
     * Get the UTM northing
     *
     * @return UTM northing
     */
    getUTMNorthing() {
        // get northing specified by n100k
        const rowLetters = MGRS.getRowLetters(this.zone);
        const rowIndex = rowLetters.indexOf(this.row);
        const n100kNum = rowIndex * 100000.0; // n100k in meters
        // get latitude of (bottom of) band
        const latBand = GridZones_1.GridZones.getSouthLatitude(this.band);
        // northing of bottom of band, extended to include entirety of
        // bottommost 100km square
        // (100km square boundaries are aligned with 100km UTM northing
        // intervals)
        const latBandNorthing = UTM_1.UTM.from(grid_js_1.Point.degrees(0, latBand)).getNorthing();
        const nBand = Math.floor(latBandNorthing / 100000) * 100000;
        // 100km grid square row letters repeat every 2,000km north; add enough
        // 2,000km blocks to get
        // into required band
        let n2M = 0; // northing of 2,000km block
        while (n2M + n100kNum + this.northing < nBand) {
            n2M += 2000000;
        }
        return n2M + n100kNum + this.northing;
    }
    /**
     * {@inheritDoc}
     */
    toString() {
        return this.coordinate(GridType_1.GridType.METER);
    }
    /**
     * Return whether the given string is valid MGRS string
     *
     * @param mgrs
     *            potential MGRS string
     * @return true if MGRS string is valid, false otherwise
     */
    static isMGRS(mgrs) {
        mgrs = MGRS.removeSpaces(mgrs);
        return MGRS.mgrsPattern.test(mgrs) && !MGRS.mgrsInvalidPattern.test(mgrs);
    }
    /**
     * Removed spaces from the value
     *
     * @param value
     *            value string
     * @return value without spaces
     */
    static removeSpaces(value) {
        return value.replace(/\s/g, '');
    }
    /**
     * Encodes a point as a MGRS string
     *
     * @param point
     *            point
     * @return MGRS
     */
    static from(point) {
        point = point.toDegrees();
        const utm = UTM_1.UTM.from(point);
        const bandLetter = GridZones_1.GridZones.getBandLetterFromLatitude(point.getLatitude());
        const columnLetter = MGRS.getColumnLetterFromUTM(utm);
        const rowLetter = MGRS.getRowLetterFromUTM(utm);
        // truncate easting/northing to within 100km grid square
        const easting = ~~(utm.getEasting() % 100000);
        const northing = ~~(utm.getNorthing() % 100000);
        return MGRS.create(utm.getZone(), bandLetter, easting, northing, columnLetter, rowLetter);
    }
    /**
     * Parse a MGRS string
     *
     * @param mgrs
     *            MGRS string
     * @return MGRS
     * @throws ParseException
     *             upon failure to parse the MGRS string
     */
    static parse(mgrs) {
        if (!MGRS.mgrsPattern.test(MGRS.removeSpaces(mgrs))) {
            throw new Error('Invalid MGRS: ' + mgrs);
        }
        const matches = MGRS.removeSpaces(mgrs).match(MGRS.mgrsPattern);
        const zone = Number.parseInt(matches[1], 10);
        const band = matches[2].toUpperCase().charAt(0);
        const gridZone = GridZones_1.GridZones.getGridZone(zone, band);
        if (gridZone == null) {
            throw new Error('Invalid MGRS: ' + mgrs);
        }
        let mgrsValue;
        let columnRow = matches[3];
        if (columnRow) {
            columnRow = columnRow.toUpperCase();
            const column = columnRow.charAt(0);
            const row = columnRow.charAt(1);
            // parse easting & northing
            let easting = 0;
            let northing = 0;
            const location = matches[4];
            if (location.length > 0) {
                const precision = location.length / 2;
                const multiplier = Math.pow(10.0, 5 - precision);
                easting = +location.substring(0, precision) * multiplier;
                northing = +location.substring(precision) * multiplier;
            }
            mgrsValue = MGRS.create(zone, band, easting, northing, column, row);
            if (location.length === 0) {
                const point = mgrsValue.toPoint().toDegrees();
                const gridBounds = gridZone.getBounds();
                const gridSouthwest = gridBounds.getSouthwest().toDegrees();
                const westBounds = point.getLongitude() < gridSouthwest.getLongitude();
                const southBounds = point.getLatitude() < gridSouthwest.getLatitude();
                if (westBounds || southBounds) {
                    if (westBounds && southBounds) {
                        const northeast = MGRS.create(zone, band, GridType_1.GridType.HUNDRED_KILOMETER, GridType_1.GridType.HUNDRED_KILOMETER, column, row).toPoint();
                        if (gridBounds.containsPoint(northeast)) {
                            mgrsValue = MGRS.from(grid_js_1.Point.degrees(gridSouthwest.getLongitude(), gridSouthwest.getLatitude()));
                        }
                    }
                    else if (westBounds) {
                        const east = MGRS.create(zone, band, GridType_1.GridType.HUNDRED_KILOMETER, northing, column, row).toPoint();
                        if (gridBounds.containsPoint(east)) {
                            const intersection = MGRS.getWesternBoundsPoint(gridZone, point, east);
                            mgrsValue = MGRS.from(intersection);
                        }
                    }
                    else if (southBounds) {
                        const north = MGRS.create(zone, band, easting, GridType_1.GridType.HUNDRED_KILOMETER, column, row).toPoint();
                        if (gridBounds.containsPoint(north)) {
                            const intersection = MGRS.getSouthernBoundsPoint(gridZone, point, north);
                            mgrsValue = MGRS.from(intersection);
                        }
                    }
                }
            }
        }
        else {
            mgrsValue = MGRS.from(gridZone.getBounds().getSouthwest());
        }
        return mgrsValue;
    }
    /**
     * Get the point on the western grid zone bounds point between the western
     * and eastern points
     *
     * @param gridZone
     *            grid zone
     * @param west
     *            western point
     * @param east
     *            eastern point
     * @return western grid bounds point
     */
    static getWesternBoundsPoint(gridZone, west, east) {
        const eastUTM = UTM_1.UTM.from(east);
        const northing = eastUTM.getNorthing();
        const zoneNumber = gridZone.getNumber();
        const hemisphere = gridZone.getHemisphere();
        const line = grid_js_1.Line.line(west, east);
        const boundsLine = gridZone.getBounds().getWestLine();
        const intersection = line.intersection(boundsLine);
        // Intersection easting
        const intersectionUTM = UTM_1.UTM.from(intersection, zoneNumber, hemisphere);
        const intersectionEasting = intersectionUTM.getEasting();
        // One meter precision just inside the bounds
        const boundsEasting = Math.ceil(intersectionEasting);
        // Higher precision point just inside of the bounds
        const boundsPoint = UTM_1.UTM.point(zoneNumber, hemisphere, boundsEasting, northing);
        boundsPoint.setLongitude(boundsLine.getPoint1().getLongitude());
        return boundsPoint;
    }
    /**
     * Get the point on the southern grid zone bounds point between the southern
     * and northern points
     *
     * @param gridZone
     *            grid zone
     * @param south
     *            southern point
     * @param north
     *            northern point
     * @return southern grid bounds point
     */
    static getSouthernBoundsPoint(gridZone, south, north) {
        const northUTM = UTM_1.UTM.from(north);
        const easting = northUTM.getEasting();
        const zoneNumber = gridZone.getNumber();
        const hemisphere = gridZone.getHemisphere();
        const line = grid_js_1.Line.line(south, north);
        const boundsLine = gridZone.getBounds().getSouthLine();
        const intersection = line.intersection(boundsLine);
        // Intersection northing
        const intersectionUTM = UTM_1.UTM.from(intersection, zoneNumber, hemisphere);
        const intersectionNorthing = intersectionUTM.getNorthing();
        // One meter precision just inside the bounds
        const boundsNorthing = Math.ceil(intersectionNorthing);
        // Higher precision point just inside of the bounds
        const boundsPoint = UTM_1.UTM.point(zoneNumber, hemisphere, easting, boundsNorthing);
        boundsPoint.setLatitude(boundsLine.getPoint1().getLatitude());
        return boundsPoint;
    }
    /**
     * Parse the MGRS string for the precision
     *
     * @param mgrs
     *            MGRS string
     * @return grid type precision
     * @throws ParseException
     *             upon failure to parse the MGRS string
     */
    static precision(mgrs) {
        if (!MGRS.mgrsPattern.test(MGRS.removeSpaces(mgrs))) {
            throw new Error('Invalid MGRS: ' + mgrs);
        }
        const matches = MGRS.removeSpaces(mgrs).match(MGRS.mgrsPattern);
        let precision;
        if (matches[3]) {
            const location = matches[4];
            if (location.length > 0) {
                precision = GridTypeUtils_1.GridTypeUtils.withAccuracy(location.length / 2);
            }
            else {
                precision = GridType_1.GridType.HUNDRED_KILOMETER;
            }
        }
        else {
            precision = GridType_1.GridType.GZD;
        }
        return precision;
    }
    /**
     * Get the MGRS coordinate accuracy number of digits
     *
     * @param mgrs
     *            MGRS string
     * @return accuracy digits
     * @throws ParseException
     *             upon failure to parse the MGRS string
     */
    static accuracy(mgrs) {
        return GridTypeUtils_1.GridTypeUtils.getAccuracy(MGRS.precision(mgrs));
    }
    /**
     * Get the two letter column and row 100k designator for a given UTM
     * easting, northing and zone number value
     *
     * @param easting
     *            easting
     * @param northing
     *            northing
     * @param zoneNumber
     *            zone number
     * @return the two letter column and row 100k designator
     */
    static getColumnRowId(easting, northing, zoneNumber) {
        const columnLetter = MGRS.getColumnLetter(zoneNumber, easting);
        const rowLetter = MGRS.getRowLetter(zoneNumber, northing);
        return columnLetter + rowLetter;
    }
    /**
     * Get the column letter from the UTM
     *
     * @param utm
     *            UTM
     * @return column letter
     */
    static getColumnLetterFromUTM(utm) {
        return MGRS.getColumnLetter(utm.getZone(), utm.getEasting());
    }
    /**
     * Get the column letter from the zone number and easting
     *
     * @param zoneNumber
     *            zone number
     * @param easting
     *            easting
     * @return column letter
     */
    static getColumnLetter(zoneNumber, easting) {
        // columns in zone 1 are A-H, zone 2 J-R, zone 3 S-Z, then repeating
        // every 3rd zone
        const column = ~~Math.floor(easting / 100000);
        const columnLetters = MGRS.getColumnLetters(zoneNumber);
        return columnLetters.charAt(column - 1);
    }
    /**
     * Get the row letter from the UTM
     *
     * @param utm
     *            UTM
     * @return row letter
     */
    static getRowLetterFromUTM(utm) {
        return MGRS.getRowLetter(utm.getZone(), utm.getNorthing());
    }
    /**
     * Get the row letter from the zone number and northing
     *
     * @param zoneNumber
     *            zone number
     * @param northing
     *            northing
     * @return row letter
     */
    static getRowLetter(zoneNumber, northing) {
        // rows in even zones are A-V, in odd zones are F-E
        const row = ~~Math.floor(northing / 100000) % 20;
        const rowLetters = MGRS.getRowLetters(zoneNumber);
        return rowLetters.charAt(row);
    }
    /**
     * Get the column letters for the zone number
     *
     * @param zoneNumber
     *            zone number
     * @return column letters
     */
    static getColumnLetters(zoneNumber) {
        return MGRS.columnLetters[(zoneNumber - 1) % 3];
    }
    /**
     * Get the row letters for the zone number
     *
     * @param zoneNumber
     *            zone number
     * @return row letters
     */
    static getRowLetters(zoneNumber) {
        return MGRS.rowLetters[(zoneNumber - 1) % 2];
    }
}
exports.MGRS = MGRS;
/**
 * 100km grid square column (‘e’) letters repeat every third zone
 */
MGRS.columnLetters = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'];
/**
 * 100km grid square row (‘n’) letters repeat every other zone
 */
MGRS.rowLetters = ['ABCDEFGHJKLMNPQRSTUV', 'FGHJKLMNPQRSTUVABCDE'];
/**
 * MGRS string pattern
 */
MGRS.mgrsPattern = new RegExp('^(\\d{1,2})([C-HJ-NP-X])(?:([A-HJ-NP-Z][A-HJ-NP-V])((\\d{2}){0,5}))?$', 'i');
/**
 * MGRS invalid string pattern (Svalbard)
 */
MGRS.mgrsInvalidPattern = new RegExp('^3[246]X.*$', 'i');
//# sourceMappingURL=MGRS.js.map